import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { moneyTight } from '../../utils/region';

// ── Which account a supplier's purchases belong in ──────────────────────────────────────────────
// Owner 2026-10-02, asking whether classification could be automatic. Most of it can, because most
// of it is not judgement — it is REPETITION. Measured that day: 4,498 purchase lines from 836
// suppliers, the top 50 carrying 70% of them. Siam Global House is building materials every time.
//
// So this screen is not "classify your expenses". It is "answer each supplier once". The list is
// grouped by SUPPLIER for that reason: Q Beverages' 28 unclassified lines are four suppliers, and
// deciding four things is a different job from deciding twenty-eight.
//
// What it will NOT do is suggest an account from the supplier's name. A line parked on 5900 is
// visible and somebody clears it; a confidently wrong classification looks finished and nobody
// looks again — and the wrong expense account is a deductibility question, not a presentation one.
type Vendor = {
  taxId: string; nameKey: string; name: string; spellings: string[];
  lines: number; base: number; vat: number; firstSeen: string; lastSeen: string;
  accountCode: string; mappingId: string; scope: string; note: string;
};
type Account = { id: string; code: string; name_th: string; name_en: string; class: string };

export default function VendorAccounts({ clientId, onChanged }: { clientId: string; onChanged?: () => void }) {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    if (!clientId) return;
    api.axVendorAccounts(clientId).then((d: any) => setVendors(d.vendors || [])).catch((e: any) => setErr(e?.message || ''));
    api.acctChart(clientId).then((d: any) => setAccounts((d.accounts || d.rows || d || []).filter((a: any) => a.class === 'expense')))
      .catch(() => setAccounts([]));
  }, [clientId]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const save = async (v: Vendor, accountCode: string, scope: 'client' | 'firm') => {
    if (!accountCode) return;
    setBusy(v.taxId || v.nameKey); setErr('');
    try {
      await api.axRememberVendor({ clientId, taxId: v.taxId, nameKey: v.nameKey, name: v.name, accountCode, scope });
      load(); onChanged?.();
    } catch (e: any) { setErr(e?.message || 'failed'); }
    finally { setBusy(''); }
  };
  const forget = async (v: Vendor) => {
    setBusy(v.taxId || v.nameKey);
    try { await api.axForgetVendor(v.mappingId, clientId); load(); onChanged?.(); }
    catch (e: any) { setErr(e?.message || 'failed'); }
    finally { setBusy(''); }
  };

  const todo = vendors.filter(v => !v.accountCode);
  const done = vendors.filter(v => v.accountCode);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface)', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 15px', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>Supplier → account</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {vendors.length ? `${done.length} of ${vendors.length} answered` : 'decide once per supplier, reused after that'}
        </div>
        <div style={{ flex: 1 }} />
        {!!todo.length && open === false && (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#8A5A00', background: '#FEF3C7',
                         border: '1px solid #FCD34D', borderRadius: 999, padding: '2px 9px' }}>
            {todo.length} to answer
          </span>
        )}
        <button style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--accent)' }}>
          {open ? 'Hide' : 'Open'}
        </button>
      </div>

      {open && (
        <div style={{ padding: '0 15px 14px' }}>
          {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
          {!vendors.length && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>No purchase lines for this client yet.</div>}
          {[...todo, ...done].map(v => {
            const key = v.taxId || v.nameKey;
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                                      padding: '9px 0', borderTop: '1px solid var(--border)' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {v.name}
                    {v.spellings.length > 1 && (
                      <span title={v.spellings.join('\n')} style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)' }}>
                        ({v.spellings.length} spellings)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                    {v.taxId || 'no tax id'} · {v.lines} line{v.lines === 1 ? '' : 's'} · {moneyTight(v.base)}
                    {v.scope === 'firm' && v.accountCode ? ' · firm-wide' : ''}
                  </div>
                </div>
                <select value={v.accountCode} disabled={busy === key}
                  onChange={e => save(v, e.target.value, 'client')}
                  style={{ flex: '0 1 250px', padding: '6px 8px', fontSize: 12.5, borderRadius: 'var(--radius-md)',
                           border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>
                  <option value="">— choose an account —</option>
                  {accounts.map(a => <option key={a.code} value={a.code}>{a.code} {a.name_en}</option>)}
                </select>
                {/* Firm-wide is a separate, deliberate act. A telecom or a fuel station is the same
                    expense to everybody; a builders' merchant is cost of sales to a contractor and
                    repairs to a hotel, so the default must not be chosen by accident. */}
                {!!v.accountCode && v.scope === 'client' && (
                  <button onClick={() => save(v, v.accountCode, 'firm')} disabled={busy === key}
                    title="Use this answer for every client, not just this one"
                    style={{ padding: '5px 9px', fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-md)',
                             border: '1px solid var(--border-strong)', background: 'var(--bg-surface)',
                             color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    all clients
                  </button>
                )}
                {!!v.mappingId && (
                  <button onClick={() => forget(v)} disabled={busy === key}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 11.5, color: '#a32018' }}>
                    clear
                  </button>
                )}
              </div>
            );
          })}
          {!!todo.length && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.6 }}>
              Unanswered suppliers park on 5900 Other expenses and say so on the draft. Answering one
              here fixes every future line from that supplier; drafts already generated keep the
              account they were made with until they are regenerated.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
